import domain from "../Domain";
import { AuthContext } from "../context/AuthContext";
import {ServerResponse} from "../types/ServerResponse";
import React from "react";

export default class NetworkUtil extends React.Component {
    static contextType = AuthContext;
    static UserContext;
    
    constructor(props) {
        super(props);
    }

    componentDidMount() {
        NetworkUtil.UserContext = this.context;
    }

    // Pure: pull a { reason } message out of a response body string. Exported so the JSON-error
    // branch is unit-testable without an XHR. Returns "" when the body isn't our JSON shape.
    public static parseExportErrorReason(text: string): string {
        try {
            const parsed = JSON.parse(text);
            return parsed && typeof parsed.reason === "string" ? parsed.reason : "";
        } catch (e) {
            return "";
        }
    }

    // Resolves { downloaded: true } after triggering the file download, or { downloaded: false, reason }
    // when the server returns JSON instead of a file (nothing to export / an error), a non-200 status,
    // or the request itself errors. It ALWAYS resolves — callers rely on it to clear their spinner, so a
    // 500 or network failure must not leave the promise pending.
    public static async downloadCSV(route:string, fileName:string, body:any):Promise<{ downloaded:boolean; reason?:string }>{
        return new Promise((resolve) =>{
            let xhttp = new XMLHttpRequest();
            // Read the (blob) response body as text and resolve as a non-download, with its reason if
            // the body is our JSON, otherwise the given fallback.
            const resolveWithReason = (fallback: string) => {
                const blob = xhttp.response;
                if (blob && typeof FileReader !== "undefined") {
                    try {
                        const reader = new FileReader();
                        reader.onload = () => resolve({ downloaded: false, reason: NetworkUtil.parseExportErrorReason(String(reader.result || "")) || fallback });
                        reader.onerror = () => resolve({ downloaded: false, reason: fallback });
                        reader.readAsText(blob);
                        return;
                    } catch (e) { /* fall through to fallback */ }
                }
                resolve({ downloaded: false, reason: fallback });
            };
            xhttp.onreadystatechange = () => {
                if (xhttp.readyState !== 4) return;
                if (xhttp.status !== 200) {
                    // Non-200 (e.g. 500 / 4xx). Surface the server's reason if present, else a generic one.
                    resolveWithReason("Failed to export.");
                    return;
                }
                const contentType = xhttp.getResponseHeader('Content-Type') || '';
                if (contentType.indexOf('application/json') !== -1) {
                    // Server returned a message, not a file (e.g. nothing to export).
                    resolveWithReason("");
                    return;
                }
                // make downloadable link
                let a = document.createElement('a')
                a.href = window.URL.createObjectURL(xhttp.response)
                a.download = fileName; // give the download a filename
                a.style.display = 'none';
                document.body.appendChild(a);
                a.click();
                resolve({ downloaded: true });
            };
            xhttp.onerror = () => resolve({ downloaded: false, reason: "Network error while exporting." });
            xhttp.open("POST",  route)
            if(NetworkUtil.UserContext){
                xhttp.setRequestHeader("Authorization", `Bearer ${NetworkUtil.UserContext.accessToken}`)
            }
            xhttp.setRequestHeader("Content-Type", "application/json");
            xhttp.responseType = 'blob';
            xhttp.send(JSON.stringify(body));
        })
    }

    // Authenticated GET download (e.g. a finished async export). The endpoint either
    // streams the file directly (local dev, same origin) or returns JSON { url } pointing
    // at a signed URL (GCS in prod); we trigger the browser to fetch that URL directly,
    // which needs no bucket CORS. Distinguished by the response Content-Type.
    public static async downloadAuthedFile(route:string, fileName:string):Promise<boolean>{
        return new Promise((resolve, reject) =>{
            let xhttp = new XMLHttpRequest();
            const triggerDownload = (href:string, revoke:boolean) => {
                let a = document.createElement('a')
                a.href = href
                a.download = fileName;
                a.style.display = 'none';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                if (revoke) { try { window.URL.revokeObjectURL(href); } catch (e) { /* noop */ } }
            };
            xhttp.onreadystatechange = () => {
                if (xhttp.readyState !== 4) return;
                if (xhttp.status === 200) {
                    const contentType = xhttp.getResponseHeader('Content-Type') || '';
                    if (contentType.indexOf('application/json') !== -1) {
                        // Signed-URL case: response blob is JSON { url }.
                        (xhttp.response as Blob).text().then((text) => {
                            let url: string | null = null;
                            try { url = JSON.parse(text).url || null; } catch (e) { /* not JSON */ }
                            if (url) { triggerDownload(url, false); resolve(true); }
                            else { reject(new Error('Download failed.')); }
                        }).catch(() => reject(new Error('Download failed.')));
                    } else {
                        // Streamed file case: response is the file blob.
                        triggerDownload(window.URL.createObjectURL(xhttp.response), true);
                        resolve(true);
                    }
                } else {
                    reject(new Error('Download failed.'));
                }
            };
            xhttp.onerror = () => reject(new Error('Download failed.'));
            xhttp.open("GET", route)
            if(NetworkUtil.UserContext){
                xhttp.setRequestHeader("Authorization", `Bearer ${NetworkUtil.UserContext.accessToken}`)
            }
            xhttp.responseType = 'blob';
            xhttp.send();
        })
    }

    public static buildURI(route: string): string {
        if(route[0] !== "/") {
            route = "/" + route;
        }
        return domain + route;
    }

    public static async makeGet<T>(route:string):Promise<T>{
        let headers = NetworkUtil.UserContext && NetworkUtil.UserContext.accessToken ?
            { 
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${NetworkUtil.UserContext.accessToken}` } :
            {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        let res = await fetch(NetworkUtil.buildURI(route),{
                method: 'GET',
                headers
            })

        let json = await res.json();
        return json as T;
    }
    
    public static async makePost<T>(route: string, body: any): Promise<T> {
        let headers = NetworkUtil.UserContext && NetworkUtil.UserContext.accessToken ? 
            { 
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization' : `Bearer ${NetworkUtil.UserContext.accessToken}` } :
            {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        let res = await fetch(NetworkUtil.buildURI(route), {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(body)
        });
        let json = await res.json();
        return json as T;
    }

    public static async makePostForBuffer<T>(route:string, body:any): Promise<Blob | ServerResponse> {
        let headers = NetworkUtil.UserContext && NetworkUtil.UserContext.accessToken ?
            { 
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${NetworkUtil.UserContext.accessToken}` 
            } :
            {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            };
        let res = await fetch(NetworkUtil.buildURI(route), {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(body)
        });

        if(res.status === 404){
            let json = await res.json();
            return json as ServerResponse;
        }
        else{
            return res.blob();
        }
    
    }

    //used to get Insurance Images for Samples Modal
    public static async makePostForBlob<T>(route:string, body:any): Promise<Blob | ServerResponse> {
        let headers = NetworkUtil.UserContext && NetworkUtil.UserContext.accessToken ? 
            { 
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization' : `Bearer ${NetworkUtil.UserContext.accessToken}` } :
            {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        let res = await fetch(NetworkUtil.buildURI(route), {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(body)
        });


        if(res.status === 404){
            let json = await res.json();
            return json as ServerResponse;
        }
        else{
            let blob = await res.blob();
            blob = URL.createObjectURL(blob) as any
            return blob as Blob;
        }
    }

    /**
     * Does not stringify the body or sent application/json headers.
     * Should only be used when posting FORM data, not data serialized as JSON.
     */
    public static async postForm<T>(route: string, body: any): Promise<T> {
        let res;
        if(NetworkUtil.UserContext && NetworkUtil.UserContext.accessToken){
            res = await fetch(NetworkUtil.buildURI(route),{
                method: 'POST',
                headers: {'Authorization': `Bearer ${NetworkUtil.UserContext.accessToken}`},
                body: body
            })
        } else {
            res = await fetch(NetworkUtil.buildURI(route), {
                method: 'POST',
                body: body
            });
        } 
        let json = await res.json();
        return json as T;
    }

    // render nothing on screen
    render() {
        return (
            <>
            </>
        )
    }
}
